import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withRouter } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../PreCloseLoan/PreCloseLoan.scss";
import { fetchAPIData } from "../../../service/service";
import { getSecureItem } from "../../../utilsFunctions";
import axios from "axios";
import { BaseUrl } from "../../../Environment";

const DECIMAL_INPUT_REGEX = /^\d*\.?\d*$/;

export function PreCloseSaving({
  open,
  savingId,
  savingsCollectionId,
  balanceAmt,
  interestAmt,
  close,
}) {
  const history = useHistory();
  // Configure toast once, not in every render.
  if (!toast.isActive()) toast.configure();

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [waiver, setWaiver] = useState("");

  const loadSavingDetails = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);

    try {
      setOutstandingBalance(balanceAmt || 0);
      setInterestRate(interestAmt || 0);
    } catch (error) {
      console.error("Fetch Saving Details Error:", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [balanceAmt, interestAmt]);

  // Load details each time the dialog opens
  useEffect(() => {
    if (open) {
      loadSavingDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, savingId]);

  const handleFieldChange = (setter) => (e) => {
    const { value } = e.target;
    if (value === "" || DECIMAL_INPUT_REGEX.test(value)) {
      setter(value);
    }
  };

  const totalSettlement = useMemo(() => {
    const balance = parseFloat(outstandingBalance) || 0;
    const rate = parseFloat(interestRate) || 0;
    const waiverAmount = parseFloat(waiver) || 0;
    return balance + rate - waiverAmount;
  }, [outstandingBalance, interestRate, waiver]);

  const onSubmitPreClose = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const token = getSecureItem("token");
      const loggedinUser = getSecureItem("loggedinUser");
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        savingId,
        savingsCollectionId, // the savingsCollectionId of the current pending row
        collectionAmount: totalSettlement, // amount actually received to close the saving
        PenaltyApplied: 0, // optional, if any penalty is being collected too
        collectedDate: new Date().toISOString(),
        collectorId: loggedinUser?.userId,
        paymentMode: "Cash",
      };

      const resp = await axios.put(
        `${BaseUrl}/savings/preCloseSaving`,
        payload,
        {
          headers,
        },
      );

      if (resp.data?.status) {
        close();
        toast.success(resp.data?.msg, {
          position: "top-center",
          autoClose: 3000,
          pauseOnHover: false,
        });
        history.goBack();
      }
    } catch (error) {
      console.error("PreClose Saving Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBreakdown = () => (
    <div className="breakdownWrapper">
      <div className="breakdownRow">
        <span className="breakdownLabel">Amount Collected +</span>
        <input
          type="text"
          className="breakdownInput"
          value={isLoading ? "" : outstandingBalance}
          placeholder={isLoading ? "..." : "0"}
          disabled
        />
      </div>

      <div className="breakdownRow">
        <span className="breakdownLabel">Interest Applied +</span>
        <input
          type="text"
          className="breakdownInput"
          value={isLoading ? "" : interestRate}
          placeholder={isLoading ? "..." : "0"}
          disabled
        />
      </div>

      <div className="breakdownRow">
        <span className="breakdownLabel">Charges -</span>
        <input
          type="text"
          className="breakdownInput"
          placeholder="0"
          value={waiver}
          disabled={isLoading}
          onChange={handleFieldChange(setWaiver)}
        />
      </div>

      <div className="breakdownRow totalRow">
        <span className="breakdownLabel">Total Maturity Amount:</span>
        <span className="totalValue">
          {isLoading ? "--" : totalSettlement.toFixed(2)}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <div className="deleteuserpage">
        <Dialog open={open} className="closeCustomerDialogbox">
          <DialogTitle id="customized-dialog-title" className="TitleDiv">
            Pre Close Saving
            <div className="deleteconfirmation">
              <div className="Fonts btnSizeFont confrmmessage">
                By pre-closing the saving, you cannot revert the process.
              </div>

              {loadError ? (
                <div className="loadErrorRow">
                  Couldn't load saving details.{" "}
                  <button
                    type="button"
                    className="retryLink"
                    onClick={loadSavingDetails}
                  >
                    Retry
                  </button>
                </div>
              ) : isLoading ? (
                <div className="loadingRow">
                  <CircularProgress size={16} thickness={5} />
                  <span>Fetching saving details…</span>
                </div>
              ) : (
                renderBreakdown()
              )}
            </div>
          </DialogTitle>

          <DialogContent className="bottomPart">
            <div className="buttonDiv">
              <Button
                className="replanbutton Fonts"
                onClick={close}
                disabled={isSubmitting}
              >
                Close
              </Button>

              <Button
                className="savebutton Fonts"
                onClick={onSubmitPreClose}
                disabled={isLoading || loadError || isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Confirm"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
